<script setup lang="ts" generic="T">
import { computed, type HTMLAttributes } from "vue";
import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome'
import {
  faCaretDown as faCaretDownActive,
  faCaretUp as faCaretUpActive
} from '@fortawesome/pro-solid-svg-icons'
import {
  faCaretDown as faCaretDownInactive,
  faCaretUp as faCaretUpInactive
} from '@fortawesome/pro-duotone-svg-icons'
import * as jsonPath from 'jsonpath'
import InputSelect from '@/components/InputSelect.vue'
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import type { SortedField } from "@/types";

const props = withDefaults(
  defineProps<{
    records: T[]
    columns: {
      name: string
      displayName?: string
      path?: string
      sortable?: boolean
      slot?: string | boolean
    }[]

    messages?: Partial<Record<'empty', string>>
    rowAttributes?: (arg: { record: T, records: T[], index: number }) => HTMLAttributes
    enablePagination?: boolean
    fieldSorting?: SortedField[]
    page?: number
    pageSize?: number
    totalRecords?: number
    pageSizeOptions?: number[]
  }>(),
  {
    rowAttributes: () => ({}),
    pageSize: 10,
    fieldSorting: () => [],
    pageSizeOptions: () => [10, 25, 50, 100]
  }
)

const emit = defineEmits<{
  'update:fieldSorting': [NonNullable<(typeof props)['fieldSorting']>]
  'update:page': [NonNullable<(typeof props)['page']>]
  'update:pageSize': [NonNullable<(typeof props)['pageSize']>]
}>()

const computedMessages = computed<Required<NonNullable<typeof props.messages>>>(
  () => ({
    empty: props.messages?.empty || 'No matching records found'
  })
)

const pagination = computed(() => {
  const { enablePagination, pageSize, totalRecords, page } = props

  // Paging is disabled.
  if (!enablePagination) {
    return undefined
  }

  // Not enough data for paging.
  if (pageSize === undefined || totalRecords === undefined || page === undefined) {
    console.warn(
      `Invalid props supplied to enable paging. [pageSize=${pageSize}] [totalRecords=${totalRecords}] [page=${page}]`
    )

    return undefined
  }

  const possibleLastRecord = page * pageSize

  return {
    firstRecord: (page - 1) * pageSize + 1,
    lastRecord: possibleLastRecord > totalRecords ? totalRecords : possibleLastRecord,
    pageSize,
    totalRecords,
    currentPage: page
  }
})

const sortingAsMap = computed(() => {
  if (props.fieldSorting === undefined) {
    return {}
  }

  return props.fieldSorting.reduce<{ [k: string]: { direction: SortedField['direction']; index: number } }>(
    (prev, current, index) => {
      prev[current.field] = { direction: current.direction, index }

      return prev
    },
    {}
  )
})

const emitPageSizeChange = (value: unknown) => {
  if (typeof value === 'number' && value > 0) {
    emit('update:pageSize', value)
  }
}

const emitPageChange = (direction: 'next' | 'prev') => {
  if (pagination.value === undefined) {
    return
  }

  const { currentPage, totalRecords, pageSize } = pagination.value
  const totalPages = Math.ceil(totalRecords / pageSize)

  if (direction === 'next' && currentPage < totalPages) {
    emit('update:page', currentPage + 1)
  }

  if (direction === 'prev' && currentPage > 1) {
    emit('update:page', currentPage - 1)
  }
}

const emitSortingChange = (e: MouseEvent, column: string) => {
  const isAppending = e.ctrlKey
  let currentValue = props.fieldSorting
  const switchDirection = (d: string) => (d === 'asc' ? 'desc' : 'asc')

  // We're replacing the current sorting directory.
  if (!isAppending) {
    const currentDirection = sortingAsMap.value[column]?.direction

    currentValue = [
      {
        field: column,
        direction: currentDirection ? switchDirection(currentDirection) : 'asc'
      }
    ]
  }

  // We're sorting on multiple columns.
  else {
    currentValue = [...currentValue] // Required to ensure any watchers see a new array.

    const columnIndex = currentValue.findIndex(x => x.field === column)

    // We're appending to the sorting, but we haven't selected this column yet.
    if (columnIndex < 0) {
      currentValue.push({ field: column, direction: 'asc' })
    }
    // We're switching the direction on a column already in the sorting.
    else {
      currentValue[columnIndex].direction = switchDirection(currentValue[columnIndex].direction)
    }
  }

  emit('update:fieldSorting', currentValue)
}

const columnDisplayName = (column: (typeof props.columns)[number]): string =>
  column.displayName !== undefined
    ? column.displayName
    : column.name
        .replace(/([a-z])([A-Z])/, (match, lc, uc) => {
          return lc + ' ' + uc
        })
        .replace(/^[a-z]|\bId\b/g, m => m.toUpperCase())

</script>

<template>
  <table class="table w-100 table-hover" :class="$style.table">
    <thead>
      <tr>
        <th
          v-for="column in columns"
          :key="`${column.name}|${column.displayName}`"
          @click="column.sortable && emitSortingChange($event, column.name)"
          :class="{ [$style.sortable]: column.sortable }">
          {{ columnDisplayName(column) }}
          <FontAwesomeLayers v-if="column.sortable" class="ms-1">
            <template v-if="column.name in sortingAsMap">
              <FontAwesomeIcon
                :icon="
                  sortingAsMap[column.name].direction === 'asc'
                    ? faCaretUpActive
                    : faCaretUpInactive
                "
                transform="up-3 shrink-2" />
              <FontAwesomeIcon
                :icon="
                  sortingAsMap[column.name].direction === 'asc'
                    ? faCaretDownInactive
                    : faCaretDownActive
                "
                transform="down-3 shrink-2" />
            </template>
            <template v-else>
              <FontAwesomeIcon :icon="faCaretUpInactive" transform="up-3 shrink-2" />
              <FontAwesomeIcon :icon="faCaretDownInactive" transform="down-3 shrink-2" />
            </template>
          </FontAwesomeLayers>
          <span v-if="column.name in sortingAsMap && fieldSorting?.length > 1" class="ms-1"
            >({{ sortingAsMap[column.name].index + 1 }})</span
          >
        </th>
        <th v-if="'context' in $slots" :class="$style.context"></th>
      </tr>
    </thead>
    <tbody>
      <template v-if="records.length <= 0">
        <tr>
          <td :colspan="columns.length" class="text-center pt-2 pb-2">
            <slot v-if="$slots['empty']" name="empty" />
            <template v-else>{{ computedMessages.empty }}</template>
          </td>
        </tr>
      </template>
      <template v-else>
        <tr v-for="(record, index) in records" v-bind="rowAttributes({ record, records, index })" :key="`record:${index}`">
          <td v-for="column in columns" :key="`column:${index}:${column.name}`">
            <slot
              v-if="column.name in $slots"
              :name="column.name"
              :value="column.path ? jsonPath.value(record, column.path) : record"
              type="data"
              :record="record"
              :records="records"
              :index="index" />
            <template v-else>{{
              column.path ? jsonPath.value(record, column.path) : record
            }}</template>
          </td>
          <td v-if="'context' in $slots" :class="$style.context">
            <slot name="context" :record="record" :records="records" type="context" />
          </td>
        </tr>
      </template>
    </tbody>
  </table>

  <div v-if="pagination && records.length > 0" class="mt-4 text-end" :class="$style.pagination">
    Rows per page
    <InputSelect
      name=""
      inline
      :selected="pagination.pageSize"
      @update:selected="emitPageSizeChange($event)"
      :items="
        Array.from(new Set(pageSizeOptions.concat(pagination.pageSize)))
          .sort((a, b) => a - b)
          .map(v => ({ v }))
      "
      item-text="v"
      item-value="v"
      class="ms-2 me-4" />
    <span>
      {{ pagination.firstRecord }} - {{ pagination.lastRecord }}
      of {{ pagination.totalRecords }}
    </span>
    <span :class="$style['prev-page']" @click="emitPageChange('prev')">
      <FontAwesomeIcon :icon="faChevronLeft" />
    </span>
    <span :class="$style['next-page']" @click="emitPageChange('next')">
      <FontAwesomeIcon :icon="faChevronRight" />
    </span>
  </div>
</template>

<style module lang="scss">
@import '@/assets/variables';

.sortable {
  --fa-secondary-opacity: 0.25;
  cursor: pointer;
}

.context {
  width: 1px;
  white-space: nowrap;
}

.pagination {
  .prev-page,
  .next-page {
    cursor: pointer;
    min-width: 2rem;
    padding: 0.5rem;
    text-align: center;
    border-radius: 50%;
    margin-left: 20px;
    display: inline-block;
    color: $body-color;

    &:hover {
      background-color: #f6f6f6;
      color: darken($body-color, 25%);
    }
  }
}

.table {
  th {
    -moz-user-select: none;
  }

  th:active {
    background-color: rgba(0, 0, 0, 0.025);
  }
}
</style>